/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'

import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  ArcadeFieldLabel,
  breakpoints,
  CardSecurityCodeInput,
  FieldLabel,
  PostalCodeInput,
  spacing,
  Stack,
} from '@cash-design-system/react'
import { CashInstrumentType } from '@cash-web/protos/squareup/franklin/cash_instrument_type.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { isFirstDigitsAmex, isValidCvv, isValidPostalCode } from '@cash-web/shared-util-validation'

import { CardNumber, FormInput, FormMaskedInput } from '../'
import { ExpirationDateInput } from '../ExpirationDateInput/ExpirationDateInput'
import { FieldController } from '../FieldController/FieldController'

const InlineInputWrapper = styled.div`
  display: grid;
  column-gap: 0.9375rem;
  row-gap: 0.9375rem;
  grid-template-columns: repeat(2, 1fr);

  & > *:last-child {
    grid-column: span 2;
  }

  ${breakpoints.tablet} {
    grid-template-columns: repeat(3, 1fr);

    & > *:last-child {
      grid-column: auto;
    }
  }
`

export function FormCard(): React.ReactElement {
  const { t } = useTranslation()
  const { watch } = useFormContext()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const watchCardNumber = watch('unencrypted_pan')
  const isAmex = isFirstDigitsAmex(watchCardNumber)

  if (isArcadeMigrationEnabled) {
    return (
      <Stack space={spacing.m}>
        <CardNumber autoFocus name="unencrypted_pan" type={CashInstrumentType.DEBIT_CARD} />
        <InlineInputWrapper>
          <ExpirationDateInput name="expiration" isRequired />
          <ArcadeFieldLabel label={t('CVV', { context: 'label for form field asking for card CVV number' })}>
            <FieldController
              FieldComponent={CardSecurityCodeInput}
              name="security_code"
              componentProps={{
                cardValue: watchCardNumber,
              }}
              rules={{
                required: {
                  value: true,
                  message: t('Enter a CVV code.', {
                    context: 'error message for form field when missing a card CVV number',
                  }),
                },
                validate: (value: string) =>
                  isValidCvv(value, watchCardNumber) ||
                  t('Invalid CVV', { context: 'error message for form field with an invalid card CVV number' }),
              }}
            />
          </ArcadeFieldLabel>
          <ArcadeFieldLabel label={t('ZIP code', { context: 'label for form field asking for card zip code' })}>
            <FieldController
              FieldComponent={PostalCodeInput}
              name="postal_code"
              rules={{
                required: {
                  value: true,
                  message: t('Enter a ZIP code.', {
                    context: 'error message for form field when missing a card zip code',
                  }),
                },
                validate: value =>
                  isValidPostalCode(value) ||
                  t('Invalid ZIP code.', { context: 'error message for form field with an invalid card zip code' }),
              }}
            />
          </ArcadeFieldLabel>
        </InlineInputWrapper>
      </Stack>
    )
  }

  return (
    <Stack space={spacing.m}>
      <CardNumber autoFocus name="unencrypted_pan" type={CashInstrumentType.DEBIT_CARD} />
      <InlineInputWrapper>
        <ExpirationDateInput name="expiration" isRequired />
        <FieldLabel label={t('CVV', { context: 'label for form field asking for card CVV number' })}>
          <FormMaskedInput
            name="security_code"
            placeholder={t('123', { context: 'placeholder for form field asking for card CVV number' })}
            autoComplete="cc-csc"
            format={isAmex ? '####' : '###'}
            rules={{
              required: {
                value: true,
                message: t('Enter a CVV code.', {
                  context: 'error message for form field when missing a card CVV number',
                }),
              },
              validate: (value: string) =>
                isValidCvv(value, watchCardNumber) ||
                t('Invalid CVV', { context: 'error message for form field with an invalid card CVV number' }),
            }}
          />
        </FieldLabel>
        <FieldLabel label={t('ZIP code', { context: 'label for form field asking for card zip code' })}>
          <FormInput
            type="text"
            placeholder={t('12345', { context: 'placeholder for form field asking for card zip code' })}
            autoComplete="postal-code"
            name="postal_code"
            rules={{
              required: {
                value: true,
                message: t('Enter a ZIP code.', {
                  context: 'error message for form field when missing a card zip code',
                }),
              },
              validate: value =>
                isValidPostalCode(value) ||
                t('Invalid ZIP code.', { context: 'error message for form field with an invalid card zip code' }),
            }}
          />
        </FieldLabel>
      </InlineInputWrapper>
    </Stack>
  )
}
